import React, {Component} from 'react';

import {Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {TriageActionsEnum} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {TimeFormatsEnum} from '../../../../../../helpers/dateTimeHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {CustomSnoozeModel} from '../../../../../../models/api/customSnoozeModel';
import {FutureTimeValuesEnum} from '../../../../../../models/api/futureTimeValueModel';
import {ReminderPresets} from '../../../../../../models/api/preferencesModel';
import {ReminderModel} from '../../../../../../models/api/reminderModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {SnoozeLocationsEnum} from '../../../../../../services/tracking';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {FutureTimeDropdown} from '../../../../../common/futureTime/futureTimeDropdown';
import {TriageTeamConfirmationDialogContainer} from '../../topbar/triageTeamConfirmationDialogContainer';
import {BulkActionConfirmationDialog} from '../bulk/bulkActionConfirmationDialog';
import {SnoozeDropdownReminder, SnoozeDropdownReminderProps} from './snoozeDropdownReminder';

/*
 * Props.
 */

interface SnoozeDropdownProps extends DropdownProps {
  conversations: ReadonlyArray<ConversationBaseModel>;
  values: ReadonlyArray<FutureTimeValuesEnum>;
  canceledReminder?: ReminderModel;
  reminderPresets: ReminderPresets;
  customSnoozes: ReadonlyArray<CustomSnoozeModel> | null;
  value?: SnoozeDropdownReminderProps;
  onSnooze: HandlerOf<number>;
  onUnsnooze: Handler;
  onBulkSnooze: HandlerOf<number>;
  onBulkUnsnooze: Handler;
  onOpenSnoozesClick?: Handler;
  assignees: ReadonlyArray<TeammateModel | undefined>;
  timeFormat: TimeFormatsEnum;
  isBulkActionRequired: boolean;
  shouldStartOnCustom?: boolean;
  shouldShowTeamSnoozeAlert: boolean;
  cellsContext: CellsContext;
  shouldHideDropdownHeader?: boolean;
}

/*
 * Component.
 */

interface SnoozeDropdownState {
  bulkSnoozeRemindAt?: number;
  isBulkUnsnoozeConfirmationOpen: boolean;
  teamSnoozeRemindAt?: number;
}

export class SnoozeDropdown extends Component<SnoozeDropdownProps, SnoozeDropdownState> {
  constructor(props: SnoozeDropdownProps) {
    super(props);
    this.state = {
      isBulkUnsnoozeConfirmationOpen: false,
    };
  }

  /*
   * Event handlers.
   */

  private readonly onSnoozeRequest: HandlerOf<number> = (remindAt) => {
    const {onSnooze, isBulkActionRequired, onRequestClose, shouldShowTeamSnoozeAlert} = this.props;

    // If we need to do a bulk action, ask for confirmation.
    if (isBulkActionRequired) {
      this.setState({bulkSnoozeRemindAt: remindAt});
      return;
    }

    if (shouldShowTeamSnoozeAlert) {
      this.setState({teamSnoozeRemindAt: remindAt});
      return;
    }

    // Otherwise, snooze and close the dropdown.
    onSnooze(remindAt);
    if (onRequestClose) {
      onRequestClose();
    }
  };

  private readonly onUnsnoozeRequest: Handler = () => {
    const {onUnsnooze, isBulkActionRequired, onRequestClose} = this.props;

    // If we need to do a bulk action, ask for confirmation.
    if (isBulkActionRequired) {
      this.setState({isBulkUnsnoozeConfirmationOpen: true});
      return;
    }

    // Otherwise, unsnooze and close the dropdown.
    onUnsnooze();
    if (onRequestClose) {
      onRequestClose();
    }
  };

  private readonly onConfirmTeamSnooze = () => {
    const {teamSnoozeRemindAt} = this.state;
    if (!teamSnoozeRemindAt) {
      return;
    }

    this.setState({teamSnoozeRemindAt: undefined});

    const {onSnooze, onRequestClose} = this.props;
    onSnooze(teamSnoozeRemindAt);

    if (onRequestClose) {
      onRequestClose();
    }
  };

  private readonly onCloseTeamConfirmation = () => {
    this.setState({teamSnoozeRemindAt: undefined});
  };

  private readonly onConfirmBulkSnooze = () => {
    const {bulkSnoozeRemindAt} = this.state;
    if (!bulkSnoozeRemindAt) {
      return;
    }

    this.setState({bulkSnoozeRemindAt: undefined});

    const {onBulkSnooze, onRequestClose} = this.props;
    onBulkSnooze(bulkSnoozeRemindAt);

    if (onRequestClose) {
      onRequestClose();
    }
  };

  private readonly onCancelBulkSnooze = () => {
    this.setState({bulkSnoozeRemindAt: undefined});
  };

  private readonly onConfirmBulkUnsnooze = () => {
    this.setState({isBulkUnsnoozeConfirmationOpen: false});

    const {onBulkUnsnooze, onRequestClose} = this.props;
    onBulkUnsnooze();

    if (onRequestClose) {
      onRequestClose();
    }
  };

  private readonly onCancelBulkUnsnooze = () => {
    this.setState({isBulkUnsnoozeConfirmationOpen: false});
  };

  /*
   * Render.
   */

  render() {
    const {value} = this.props;
    return (
      <>
        {this.maybeRenderTeamConfirmation()}
        {this.maybeRenderBulkSnoozeConfirmation()}
        {this.maybeRenderBulkUnsnoozeConfirmation()}
        <FutureTimeDropdown
          {...this.props}
          shouldStartOnCustom={this.props.shouldStartOnCustom}
          title={<Trans id="snooze-dropdown-title">Snooze</Trans>}
          selectButton={<Trans id="snooze-dropdown-snooze">Snooze</Trans>}
          clearButton={<Trans id="snooze-dropdown-unsnooze">Unsnooze</Trans>}
          updateButton={<Trans id="snooze-dropdown-save">Save</Trans>}
          customContent={value && <SnoozeDropdownReminder {...value} />}
          value={value && value.remindAt}
          customSnoozePreset={this.props.canceledReminder}
          onValueSelect={this.onSnoozeRequest}
          onValueClear={this.onUnsnoozeRequest}
          onRequestClose={this.props.onRequestClose}
          interactionId="snooze-dropdown"
          location={SnoozeLocationsEnum.TOPBAR}
        />
      </>
    );
  }

  private maybeRenderBulkSnoozeConfirmation() {
    const {bulkSnoozeRemindAt} = this.state;
    if (!bulkSnoozeRemindAt) {
      return null;
    }

    return (
      <BulkActionConfirmationDialog
        onConfirmClick={this.onConfirmBulkSnooze}
        onRequestClose={this.onCancelBulkSnooze}
        actionType={BulkActionTypesEnum.SNOOZE}
      />
    );
  }

  private maybeRenderBulkUnsnoozeConfirmation() {
    const {isBulkUnsnoozeConfirmationOpen} = this.state;
    if (!isBulkUnsnoozeConfirmationOpen) {
      return null;
    }

    return (
      <BulkActionConfirmationDialog
        onConfirmClick={this.onConfirmBulkUnsnooze}
        onRequestClose={this.onCancelBulkUnsnooze}
        actionType={BulkActionTypesEnum.UNSNOOZE}
      />
    );
  }

  private maybeRenderTeamConfirmation() {
    if (!this.state.teamSnoozeRemindAt) {
      return null;
    }

    return (
      <TriageTeamConfirmationDialogContainer
        cellsContext={this.props.cellsContext}
        conversations={this.props.conversations}
        onConfirmClick={this.onConfirmTeamSnooze}
        onRequestClose={this.onCloseTeamConfirmation}
        action={TriageActionsEnum.SNOOZE}
      />
    );
  }
}
